import React, { useState } from "react";
import { NumericFormat } from "react-number-format";
import getDenomination, { getCurrencyCode } from "../../utilities/getDenomination";
import Switch from "../ui/Switch";
import Spinner from "../ui/Spinner";
import Button from "../ui/Button";
import editIcon from "../../assets/icons/edit-medium.svg";
import padlockIcon from "../../assets/icons/padlock.svg";
import eyeIcon from "../../assets/icons/eye-solid-full.svg";
import eyeSlashIcon from "../../assets/icons/eye-slash-solid-full.svg";

const Money = ({ code, amount, className, showCode = false }) => {
    const value = Number(amount) || 0;
    return (
        <span className={className}>
            {value < 0 && "-"}
            {getDenomination(code)}
            <NumericFormat displayType="text" value={Math.abs(value)} thousandSeparator decimalScale={0} />
            {showCode && <>&nbsp;{getCurrencyCode(code)}</>}
        </span>
    );
};

const PortalQuotePricing = ({
    costAmount = 0,
    operatorCode = "",
    saleCode = "",
    pricing = { salePrice: 0, uplift: 0, margin: 0 },
    canEdit = false,
    onSavePricing,
    isHidden = false,
    onToggleHidden,
    isRecommended = false,
    onToggleRecommended,
    savingRecommended = false,
    canReserve = false,
    onReserve,
}) => {
    const [editing, setEditing] = useState(false);
    const [draftSale, setDraftSale] = useState(pricing.salePrice);
    const [draftUplift, setDraftUplift] = useState(pricing.uplift);
    const [lastEdited, setLastEdited] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const startEdit = () => {
        setDraftSale(pricing.salePrice);
        setDraftUplift(pricing.uplift);
        setLastEdited(null);
        setError("");
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setError("");
    };

    const saveEdit = async () => {
        if (!lastEdited) {
            setEditing(false);
            return;
        }
        setSaving(true);
        setError("");
        try {
            await onSavePricing(lastEdited === "sale" ? { salePrice: draftSale } : { uplift: draftUplift });
            setEditing(false);
        } catch {
            setError("Pricing could not be saved. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const cost = (Number(pricing.salePrice) || 0) - (Number(pricing.margin) || 0);
    const round1 = (n) => Math.round(n * 10) / 10;

    const onSaleChange = (value) => {
        setDraftSale(value);
        setLastEdited("sale");
        if (cost && value !== "") setDraftUplift(round1(((Number(value) - cost) / cost) * 100));
    };

    const onUpliftChange = (value) => {
        setDraftUplift(value);
        setLastEdited("uplift");
        if (value !== "" && Number.isFinite(Number(value))) {
            setDraftSale(Math.round(cost * (1 + Number(value) / 100)));
        }
    };

    const shownUplift = editing ? Number(draftUplift) || 0 : Number(pricing.uplift);
    const shownMargin = editing ? (Number(draftSale) || 0) - cost : pricing.margin;
    const isNegative = shownUplift < 0;

    return (
        <div className="portal-quote-pricing">
            <div className="portal-quote-pricing__top">
                <div className="portal-quote-pricing__label-row">
                    <span className="portal-quote-pricing__label">Cost price</span>
                </div>
                <div className="portal-quote-pricing__controls">
                    {onToggleRecommended && (
                        <Switch
                            isOn={isRecommended}
                            controlled
                            value={onToggleRecommended}
                            disabled={!canEdit || savingRecommended}
                            classNames={["switch--gold", "switch--small"]}
                            ariaLabel="SHY Recommended"
                        />
                    )}
                    {savingRecommended && (
                        <span className="portal-quote-pricing__saving" role="status">
                            <Spinner />
                            Saving…
                        </span>
                    )}
                    {onToggleHidden && (
                        <button
                            type="button"
                            className="portal-quote-pricing__hide"
                            onClick={onToggleHidden}
                            disabled={!canEdit}
                            aria-pressed={isHidden}
                            aria-label={isHidden ? "Display aircraft option" : "Hide aircraft option"}
                            title={isHidden ? "Display aircraft option" : "Hide aircraft option"}
                        >
                            <img src={isHidden ? eyeSlashIcon : eyeIcon} alt="" />
                        </button>
                    )}
                </div>
            </div>

            <Money code={operatorCode} amount={costAmount} className="portal-quote-pricing__amount" showCode />
            <p className="portal-quote-pricing__caption">Operator currency</p>

            <div className="portal-quote-pricing__split">
                <div className="portal-quote-pricing__col">
                    <span className="portal-quote-pricing__label">Sale price ({getCurrencyCode(saleCode)})</span>
                    {editing ? (
                        <NumericFormat
                            className="portal-quote-pricing__input portal-quote-pricing__input--sale"
                            value={draftSale}
                            thousandSeparator
                            decimalScale={0}
                            allowNegative={false}
                            aria-label="Sale price"
                            onValueChange={({ floatValue }, { source }) => {
                                if (source !== "event") return;
                                onSaleChange(floatValue ?? "");
                            }}
                        />
                    ) : (
                        <Money code={saleCode} amount={pricing.salePrice} className="portal-quote-pricing__amount" />
                    )}
                    <p className="portal-quote-pricing__caption">Preferred currency</p>
                </div>
                <div className="portal-quote-pricing__col">
                    <span className="portal-quote-pricing__label">Uplift</span>
                    {editing ? (
                        <div className="portal-quote-pricing__uplift-edit">
                            <input
                                className="portal-quote-pricing__input"
                                type="number"
                                step="0.1"
                                value={draftUplift}
                                onChange={(e) => onUpliftChange(e.target.value)}
                                aria-label="Uplift percent"
                            />
                            <span>%</span>
                        </div>
                    ) : (
                        <span
                            className={`portal-quote-pricing__amount${isNegative ? " portal-quote-pricing__amount--negative" : ""}`}
                        >
                            {Number(pricing.uplift).toFixed(1)}%
                        </span>
                    )}
                    <p className="portal-quote-pricing__caption">
                        <Money
                            code={saleCode}
                            amount={shownMargin}
                            className={shownMargin < 0 ? "portal-quote-pricing__amount--negative" : undefined}
                        />{" "}
                        margin
                    </p>
                </div>
            </div>

            {error && (
                <p className="portal-quote-pricing__error" role="alert">
                    {error}
                </p>
            )}

            {canEdit &&
                (editing ? (
                    <div className="portal-quote-pricing__edit-actions">
                        <button
                            type="button"
                            className="portal-quote-pricing__edit"
                            onClick={cancelEdit}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="portal-quote-pricing__edit portal-quote-pricing__edit--save"
                            onClick={saveEdit}
                            disabled={saving}
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                ) : (
                    <button type="button" className="portal-quote-pricing__edit" onClick={startEdit}>
                        <img src={editIcon} alt="" />
                        Edit pricing
                    </button>
                ))}

            {canReserve ? (
                <div className="portal-quote-pricing__reserve">
                    <Button onClick={onReserve}>Reserve for customer</Button>
                </div>
            ) : (
                <div className="portal-quote-pricing__lock">
                    <img src={padlockIcon} alt="" />
                    <span>Reserve available once quote is released to customer</span>
                </div>
            )}
        </div>
    );
};

export default PortalQuotePricing;
